import { useEffect, useState } from "react";

import { css } from "@compiled/react";
import { useInView } from "react-intersection-observer";

import { media } from "@atlaskit/primitives";

import ErrorBoundary from "@/components/error-boundary";
import { Picture } from "@/components/wpl/image/src";
import { wacToken } from "@/libraries/design/foundations";
import { Row } from "@/libraries/wpl/foundations/src";

interface StatProps {
    stat: string;
    statText: string;
    statColor?: string;
    statSymbol?: {
        symbolCharacter: string;
        symbolCharacterColor?: string;
    };
}

export interface StatsModuleProps {
    headerText: string;
    bigStat: StatProps;
    littleStats: StatProps[];
}

const earthStyles = css({
    position: "absolute",
    insetInlineStart: 0,
    insetBlockStart: "-7%",
    // Picture component does not accept compiled CSS. Using nested selector to apply CSS to displayed image.
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    img: {
        height: "auto",
        maxWidth: "100%"
    },
    transform: "rotate(33deg)",
    aspectRatio: "1 / 1",

    [media.below.sm]: {
        height: "auto",
        width: "calc(100% + 100px)",
        insetBlockStart: "-9%",
        insetInlineStart: "52%",
        transform: "translateX(-50%) rotate(142deg)"
    }
});

export const EarthImage = () => {
    return (
        <div css={earthStyles}>
            <Picture
                image={{
                    src: "https://dam-cdn.atl.orangelogic.com/AssetLink/435e558616x6e3c2jvth65jiq2vusf20.webp",
                    alt: "",
                    width: 700,
                    height: 700
                }}
            />
        </div>
    );
};

const easeOutQuad = (t: number) => t * (2 - t);
const frameDuration = 1000 / 60;

const AnimateCount = ({ children = "0", duration = 2000 }) => {
    const countTo = parseInt(children, 10);
    const [count, setCount] = useState(0);

    useEffect(() => {
        let frame = 0;
        const totalFrames = Math.round(duration / frameDuration);
        const counter = setInterval(() => {
            frame++;
            const progress = easeOutQuad(frame / totalFrames);
            setCount(countTo * progress);

            if (frame === totalFrames) {
                clearInterval(counter);
            }
        }, frameDuration);

        return () => clearInterval(counter);
    }, [countTo, duration]);

    return Math.floor(count)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

export const Stats = (statsProps: StatsModuleProps) => {
    const { ref: scrollRef, inView: sectionIsVisible } = useInView();

    return (
        <div css={statsStyles} ref={scrollRef}>
            <Row>
                <div css={wrapperStyle} className="wrapper">
                    <div css={TextWrapperStyles} className="text-wrapper">
                        <div css={fiftyStyles} className="fifty">
                            <h2 css={h2Styles}>{statsProps.headerText}</h2>
                            <p
                                css={[statStyles, bigStatStyles]}
                                className="big-stat"
                            >
                                <span
                                    className="big-stat"
                                    css={css({
                                        color: statsProps.bigStat.statColor
                                            ? statsProps.bigStat.statColor
                                            : wacToken("color.primary.black")
                                    })}
                                >
                                    {" "}
                                    <ErrorBoundary fallback={null}>
                                        {sectionIsVisible ? (
                                            <AnimateCount>
                                                {statsProps.bigStat.stat}
                                            </AnimateCount>
                                        ) : (
                                            statsProps.bigStat.stat
                                        )}
                                    </ErrorBoundary>
                                </span>
                                {statsProps.bigStat.statSymbol && (
                                    <span
                                        css={css({
                                            color: statsProps.bigStat.statSymbol
                                                ?.symbolCharacterColor
                                                ? statsProps.bigStat.statSymbol
                                                      .symbolCharacterColor
                                                : wacToken(
                                                      "color.primary.black"
                                                  )
                                        })}
                                    >
                                        {
                                            statsProps.bigStat.statSymbol
                                                ?.symbolCharacter
                                        }
                                    </span>
                                )}
                            </p>
                            <p css={statTextStyles}>
                                {statsProps.bigStat.statText}
                            </p>
                        </div>
                        <div css={thirtyStyles} className="thirty">
                            {statsProps.littleStats.map((statObj, index) => (
                                <div
                                    css={statWrapperStyles}
                                    key={index}
                                    className="stat-wrapper"
                                >
                                    <div css={dividerStyles} />
                                    <p
                                        css={[statStyles, littleStatStyles]}
                                        className="little-stat"
                                    >
                                        <span
                                            css={css({
                                                color: statObj.statColor
                                                    ? statObj.statColor
                                                    : wacToken(
                                                          "color.primary.black"
                                                      )
                                            })}
                                        >
                                            <ErrorBoundary fallback={null}>
                                                {sectionIsVisible ? (
                                                    <AnimateCount>
                                                        {statObj.stat}
                                                    </AnimateCount>
                                                ) : (
                                                    statObj.stat
                                                )}
                                            </ErrorBoundary>
                                            {statObj.statSymbol && (
                                                <span
                                                    css={css({
                                                        color: statObj
                                                            .statSymbol
                                                            ?.symbolCharacterColor
                                                            ? statObj.statSymbol
                                                                  .symbolCharacterColor
                                                            : wacToken(
                                                                  "color.primary.black"
                                                              )
                                                    })}
                                                >
                                                    {
                                                        statObj.statSymbol
                                                            ?.symbolCharacter
                                                    }
                                                </span>
                                            )}
                                        </span>
                                    </p>
                                    <p css={statTextStyles}>
                                        {statObj.statText}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div css={earthContents} className="earth-contents">
                        <EarthImage />
                        <svg
                            css={blobStyles}
                            className="blob"
                            width="102"
                            height="95"
                            viewBox="0 0 102 95"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <g>
                                <path
                                    d="M97.6377 58.2134C98.2297 54.8868 98.5329 51.356 98.5329 47.6063C98.5329 43.8566 98.2152 40.2236 97.6233 36.8533C94.057 16.8063 80.2829 4.44834 60.9645 1.28226C57.6726 0.742421 54.2074 0.46521 50.6123 0.46521C47.0172 0.46521 43.2632 0.771614 39.8414 1.34063C20.5085 4.60885 6.74887 16.923 3.18262 36.8533C2.57621 40.2236 2.27301 43.7982 2.27301 47.6063C2.27301 51.4143 2.57621 54.8868 3.16818 58.2134C6.70555 78.1144 20.4797 90.4578 39.8414 93.726C43.2632 94.3096 46.8728 94.6014 50.6123 94.6014C54.3518 94.6014 57.6726 94.3242 60.9645 93.7844C80.3118 90.6037 94.1148 78.2312 97.6521 58.2134H97.6377Z"
                                    fill="#FCA700"
                                />
                            </g>
                        </svg>
                        <svg
                            css={lightningStyles}
                            className="lightning"
                            width="131"
                            height="139"
                            viewBox="0 0 131 139"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M20.4793 61.2896C11.5228 63.9778 10.6462 63.8696 8.14861 74.6224C5.48565 77.7767 3.98043 82.2375 2.50009 87.7471C1.52422 89.9276 8.05758 89.112 4.36913 89.8361C6.75918 90.66 13.6813 90.2023 14.4338 91.2343C10.9604 99.0243 8.42147 107.638 6.13066 115.911C6.18028 130.151 27.1036 98.5582 29.5102 96.5941C30.7176 93.1152 44.2806 78.484 38.1442 78.1761C29.196 76.6947 23.4234 81.4636 30.1387 69.8369C30.2793 69.3875 33.3061 62.3049 32.6197 60.1909C28.0133 62.1883 27.0788 70.1864 24.2752 74.5474C19.5778 80.881 28.6253 81.9463 32.6114 81.3554C32.5618 82.2875 36.1345 80.7562 35.2909 82.7037C30.1718 90.2606 12.9866 112.723 13.028 112.765C11.7296 115.611 9.81084 116.752 9.33117 115.861C9.04172 115.328 18.2546 92.6159 19.5117 89.6947C20.5289 86.1243 14.2436 86.9482 14.2436 86.9482C14.2436 86.9482 14.897 87.2727 16.7578 87.3226C14.8143 87.8969 9.87707 87.1396 6.51115 86.8733C7.08179 81.7382 13.7061 65.3676 13.7061 65.3676C13.7061 65.3676 27.7817 64.2191 31.3378 59.9662C28.7245 59.7415 24.3001 61.0649 20.471 61.2979L20.4793 61.2896Z"
                                fill="#101214"
                            />
                            <path
                                d="M128.71 53.5661L129.636 51.8599C129.934 51.294 130.314 50.7031 130.413 50.1954C130.537 49.6877 130.521 49.2633 130.488 48.9138C130.48 48.8139 130.488 48.7556 130.438 48.6391C130.397 48.5225 130.306 48.3894 130.248 48.3395C130.116 48.2313 129.983 48.1147 129.818 47.9982C129.487 47.7735 129.065 47.5322 128.47 47.424C127.858 47.3408 127.205 47.2908 126.576 47.2159C123.392 46.8913 120.216 46.5667 117.082 46.2505C114.138 45.9425 111.227 45.6346 108.341 45.335C105.76 45.052 103.205 44.7774 100.691 44.5027C100.492 44.4528 100.178 44.5193 100.137 44.253C100.153 44.0283 100.31 43.8369 100.393 43.6455C100.972 42.3971 101.543 41.1571 102.113 39.9336C103.395 37.1955 104.644 34.5239 105.86 31.9189C108.316 26.7256 110.689 21.8236 112.972 17.2878L112.699 17.5541C112.889 17.2295 113.303 16.1393 113.576 15.9479C113.923 14.9075 114.436 13.4843 115.039 11.9113C115.345 11.129 115.66 10.3051 115.982 9.47285L116.47 8.22446C116.76 7.53368 117.041 6.85116 117.322 6.17702C117.653 5.36141 117.702 4.6707 117.653 4.06315C117.587 3.43063 117.347 2.78144 116.958 2.27376C116.189 1.21679 114.948 0.800607 114.088 0.867188C113.063 1.14183 112.219 1.35825 111.632 1.51638C111.326 1.63289 111.086 1.7245 110.913 1.78276C110.789 1.82437 110.731 1.8493 110.731 1.8493C106.794 3.33905 102.626 4.49591 98.4578 5.61114C98.4578 5.61114 97.0768 5.87744 94.8769 6.31021C93.7687 6.5266 92.462 6.78464 91.0065 7.07593C90.2787 7.22574 89.5096 7.39216 88.7157 7.55861C88.3187 7.64184 87.9052 7.74169 87.4834 7.84988C87.0865 7.94975 86.6896 8.05795 86.2843 8.16614C83.0673 8.99008 79.4202 9.78077 76.1535 11.1124C74.5243 11.7699 73.0026 12.5771 71.729 13.4843C70.4472 14.3915 69.4216 15.4152 68.6939 16.3473C66.6181 18.4696 65.0798 21.008 63.8228 23.613C62.5575 26.2263 61.5733 28.9228 60.5974 31.5943C59.6464 34.2826 58.6292 36.8626 57.5458 39.5008L54.3536 47.3907C54.3536 47.3907 49.4163 60.7236 48.4156 64.9348C48.0931 66.2831 47.8864 67.4066 47.7871 68.2472C48.1841 68.2139 48.7382 68.1889 49.4328 68.1806C50.6899 65.9085 52.6582 61.3311 55.4452 53.7408C55.5527 53.5993 55.8008 53.1084 55.8918 53.0668C57.0744 49.6378 58.3315 46.2838 59.6465 42.9547C60.3163 41.2985 60.9862 39.6424 61.656 37.9862C62.3507 36.2967 62.9958 34.5821 63.6243 32.9176C64.8896 29.5886 66.1467 26.3178 67.7842 23.4132C68.6029 21.9651 69.5126 20.6085 70.5546 19.4267C71.5967 18.2365 72.7297 17.2711 74.086 16.4389C73.8213 16.3224 74.086 15.9145 74.4995 15.8479L74.5243 16.1808C76.3189 14.999 78.2789 14.2666 80.181 13.6924C82.0914 13.1181 83.9853 12.7103 85.8874 12.2609L87.3181 11.9113L88.7157 11.5452C89.6254 11.3205 90.5352 11.0957 91.4697 10.871C93.3801 10.3966 95.3814 9.84733 97.5233 9.11493C97.5068 9.12326 96.6053 9.71416 96.5888 9.7308C100.037 8.97344 103.627 8.15785 107.398 7.15081C108.341 6.88448 109.292 6.62649 110.259 6.32688C110.739 6.18539 111.227 6.03554 111.715 5.86909L112.145 5.72767L112.236 5.69435C112.352 5.64441 112.509 5.57783 112.616 5.69435C112.732 5.80254 112.658 5.96065 112.6 6.08549C109.813 12.3025 106.877 18.869 103.883 25.5521C102.32 29.0476 100.74 32.5681 99.1691 36.0969C98.3834 37.8613 97.5978 39.6257 96.8121 41.3818L95.8527 43.5206C95.6625 43.9284 95.4971 44.3446 95.431 44.7275C95.3648 45.1103 95.3649 45.4515 95.3897 45.7595C95.4145 46.3587 95.828 47.1993 96.3408 47.6321C96.5806 47.8484 96.837 47.9983 97.1595 48.1647C97.4903 48.3145 97.879 48.4394 98.3173 48.4893C105.239 49.1801 111.988 49.8542 118.521 50.5117C120.068 50.6615 121.598 50.8113 123.119 50.9528L124.252 51.061L124.74 51.1026H124.839L125.269 51.1275C125.327 51.1442 125.451 51.0943 125.46 51.1858L125.377 51.3689L125.195 51.76C124.906 52.3177 124.608 52.867 124.318 53.4246C123.979 54.0654 123.648 54.7063 123.293 55.3305L123.417 55.2056V55.2888C123.649 54.9726 123.963 54.5066 124.327 53.9073C124.674 53.3497 125.087 52.6838 125.534 51.9515C125.724 51.6851 125.956 51.3606 126.212 50.9778C126.262 50.8779 126.378 50.7947 126.378 50.6782C126.328 50.5866 126.204 50.6199 126.121 50.5783C125.915 50.5367 125.716 50.4951 125.517 50.4618C125.294 50.4202 125.071 50.3785 124.864 50.3369C124.393 50.2703 123.938 50.2038 123.5 50.1455C122.59 50.0207 121.763 49.9041 121.052 49.8126C119.638 49.6212 118.72 49.4963 118.72 49.4963C120.109 49.4547 121.523 49.463 122.946 49.5046C123.64 49.5379 124.343 49.5629 125.046 49.5962C125.592 49.6212 126.163 49.6544 126.742 49.6961L127.618 49.7709C127.693 49.7792 127.767 49.771 127.833 49.7959C127.899 49.8209 127.949 49.8958 127.932 49.9707C127.874 50.1038 127.8 50.2287 127.734 50.3536C127.453 50.8613 127.172 51.3772 126.882 51.8932C126.551 52.4758 126.212 53.0667 125.873 53.6493C125.501 54.2985 125.129 54.9476 124.732 55.5884C123.136 58.1352 121.564 60.7152 119.886 63.212C119.067 64.477 118.248 65.7338 117.438 66.9822C116.611 68.2222 115.8 69.4623 115.023 70.7107L114.742 70.4277C117.512 66.1749 119.629 62.1717 120.44 60.5571C115.593 68.2639 108.068 79.6908 99.8224 90.4602C95.7453 95.8783 91.4531 101.105 87.5 105.816C85.5069 108.154 83.6213 110.393 81.8185 112.399C80.9253 113.414 80.0735 114.388 79.263 115.312C78.4443 116.227 77.6669 117.093 76.9391 117.9C77.2037 117.842 78.1548 116.718 79.2382 115.578C80.3133 114.429 81.4959 113.223 82.3229 113.056C81.5786 113.888 80.8261 114.721 80.0818 115.545C79.3292 116.36 78.5684 117.176 77.8241 117.983C77.0715 118.79 76.3271 119.589 75.5911 120.38C74.8468 121.162 74.086 121.92 73.3416 122.661C72.8951 122.211 74.7641 120.646 75.0205 120.222C74.3093 120.788 72.1094 122.952 68.7187 126.114C67.0151 127.687 65.055 129.552 62.789 131.541C62.5161 131.782 62.2432 132.024 61.9703 132.273L61.7718 132.44C61.6312 132.531 61.532 132.681 61.3666 132.714C61.2012 132.739 61.0275 132.631 60.9614 132.465C60.8869 132.29 60.9944 132.115 61.0358 131.948C61.6395 129.901 62.268 127.754 62.9131 125.54C63.6822 122.96 64.4927 120.305 65.3446 117.6C67.0482 112.191 68.9006 106.573 70.8606 100.963C72.8124 95.3539 74.8716 89.7445 76.9474 84.3348C77.9811 81.63 79.0232 78.975 80.0569 76.395L80.4374 75.4296L80.6276 74.9469C80.6607 74.8636 80.6938 74.8054 80.7186 74.6889L80.8013 74.3227C81.0246 73.3406 81.2479 72.3668 81.4712 71.4014C81.5787 70.9187 81.6944 70.436 81.8019 69.9616L81.9674 69.2459L82.0501 68.8881C82.0583 68.8298 82.0914 68.7632 82.0832 68.7216C82.0584 68.6633 82.0087 68.6633 81.9591 68.6384C81.2313 68.3471 80.5036 68.0557 79.7841 67.7644C77.7 67.8477 75.6656 67.9226 73.6807 67.9975C72.2418 68.0391 70.8359 68.0724 69.4796 68.1057C66.767 68.1639 64.2198 68.1889 61.8959 68.1889C57.2398 68.1723 53.4522 68.164 50.8719 68.1557C50.3426 68.1557 49.8794 68.1557 49.4494 68.1557C49.2757 68.4719 49.1186 68.7382 48.9698 68.9629C49.8216 69.1128 50.9545 69.2792 52.4266 69.4789L51.5086 69.7619C56.082 70.0698 60.2584 70.253 64.501 70.4194C66.6181 70.4943 68.7517 70.5692 70.9516 70.6525C72.0515 70.6858 73.168 70.7274 74.3093 70.769C74.8799 70.7856 75.4588 70.8106 76.0377 70.8272C76.4512 70.8522 76.8647 70.8771 77.2865 70.9021C77.394 70.8938 77.4932 70.8855 77.5759 70.8772C78.2706 70.8189 78.3451 70.8355 78.6759 70.5276C79.0067 70.461 79.6517 70.3445 79.7261 70.203C79.8502 70.1031 79.9991 70.0116 80.1645 69.9367C80.2141 69.9201 80.3299 69.8785 80.3299 69.9367C80.3299 69.97 80.3299 70.0199 80.3216 70.0781C80.2803 70.3029 80.1645 70.6857 80.0074 71.1268C79.6931 72.0173 79.2217 73.0743 78.9819 73.7235C78.1549 72.5416 75.2273 79.3828 72.5809 87.1395C69.8931 94.8712 67.5443 103.469 66.8331 105.433C65.452 105.832 64.2198 113.214 63.0289 115.461C63.0372 115.278 63.1116 114.13 63.1281 113.98C62.7891 114.463 61.9538 117.026 60.9945 120.48C60.5148 122.203 59.9937 124.15 59.4727 126.156C58.9352 128.295 58.3811 130.5 57.8518 132.606C57.703 133.255 57.6368 133.821 57.6202 134.346C57.612 134.878 57.8105 135.427 58.1744 135.844C58.8773 136.709 60.1427 136.925 60.9779 136.501C61.7553 136.06 62.4582 135.394 63.0867 134.795C63.6491 134.271 64.1453 133.813 64.5588 133.438C66.0971 132.048 66.4692 131.907 64.7408 133.987C71.4147 128.528 78.742 121.678 86.1437 113.705C93.5702 105.757 100.972 96.6024 108.035 86.79C115.081 76.961 121.688 66.3996 127.61 55.6551L128.743 53.5661H128.71Z"
                                fill="#101214"
                            />
                        </svg>
                    </div>
                </div>
            </Row>
        </div>
    );
};

const wrapperStyle = css({
    display: "flex",
    paddingBlockStart: 20,
    paddingBlockEnd: 20,

    [media.below.sm]: {
        flexDirection: "column"
    }
});

const earthContents = css({
    aspectRatio: "1/1",
    position: "relative",
    insetInlineEnd: "0",
    insetBlockEnd: "0",
    insetBlockStart: "0",
    height: "100%",
    display: "flex",
    justifyContent: "center",

    [media.only.sm]: {
        insetInlineStart: 20
    },

    [media.below.sm]: {
        width: "100%",
        height: "120px",
        marginBlockEnd: "0",
        marginBlockStart: 40
    },
    [media.above.md]: {
        insetInlineEnd: "0%",
        maxHeight: 360,
        maxWidth: 360
    }
});

const blobStyles = css({
    position: "absolute",
    insetBlockStart: "0%",
    mixBlendMode: "multiply",
    insetInlineEnd: "-3%",

    [media.only.sm]: {
        insetInlineEnd: "21%"
    },

    [media.below.sm]: {
        insetInlineEnd: "2%",
        insetBlockStart: "-10%",
        width: 120,
        height: "auto"
    }
});

const lightningStyles = css({
    width: "36%",
    height: "auto",
    position: "absolute",
    insetInlineStart: "-2%",
    insetBlockEnd: "-4%",

    [media.below.sm]: {
        display: "none"
    }
});

const TextWrapperStyles = css({
    paddingBlockStart: "30px",
    maxWidth: "100%",
    display: "flex",
    alignItems: "flex-start",
    gap: "40px",
    position: "relative",

    [media.above.sm]: {
        flex: "0 0 80%",
        gap: 20
    },

    [media.below.sm]: {
        flexDirection: "column",
        paddingBlockStart: "60px"
    }
});

const fiftyStyles = css({
    flex: "1 1 50%",
    [media.below.sm]: {
        flex: "1",
        width: "100%"
    }
});

const h2Styles = css({
    fontSize: "2.5rem",
    lineHeight: 1.2,
    margin: 0
});

const statStyles = css({
    fontFamily: `${wacToken("font.charlie.display")} !important`,
    lineHeight: "1 !important",
    fontWeight: "700 !important",
    margin: "10px 0 8px",
    position: "relative",
    color: wacToken("color.primary.blue")
});

const bigStatStyles = css({
    "fontSize": "5rem !important",

    "@media(max-width: 500px)": {
        fontSize: "3.5rem !important"
    },
    [media.only.sm]: {
        fontSize: "4rem !important"
    }
});

const littleStatStyles = css({
    fontSize: "3rem !important"
});

const statTextStyles = css({
    fontSize: "1rem",
    fontWeight: "400 !important",
    margin: "0"
});

const thirtyStyles = css({
    flex: "1 1 30%",
    display: "flex",
    gap: 20,
    flexDirection: "column",
    paddingInlineEnd: 40,

    [media.below.sm]: {
        flex: "1",
        paddingInlineEnd: 0
    }
});

const statWrapperStyles = css({
    position: "relative",
    paddingInlineStart: 20
});

const dividerStyles = css({
    position: "absolute",
    height: "100%",
    background: wacToken("color.primary.black"),
    width: "2px",
    insetInlineStart: "0"
});

const statsStyles = css({
    "max-width": "1440px",
    "margin": "0 auto",
    "position": "relative",
    "zIndex": 1,
    "padding": "70px 0 100px",
    "overflow": "hidden",
    "background": wacToken("color.neutral.light100"),
    "clipPath":
        "polygon(0 0,86% 3%,86% 6%,100% 5%,100% 93%,19.2% 100%,19% 84%,0 84%)",

    [media.below.sm]: {
        "clip-path": "polygon(0% 0%, 100% 2%, 100% 96%, 0% 100%)",
        "padding": 0,
        "marginBlockEnd": "40px"
    }
});
